<script setup>
import { computed } from 'vue'
const props = defineProps(['to', 'hash', 'href', 'disabled', 'processing', 'iconLeft', 'icon', 'iconRight', 'target', 'label', 'mega', 'type', 'accent'])
</script>

<template>
	<router-link v-if="to" :class="['btn group']" :disabled :to :style="[accent ? { backgroundColor: accent, borderColor: accent } : null]">
		<div class="relative flex">
			<!-- Content -->
			<div :class="['content']">
				<i class="icon far fa-circle-notch fa-spin group animate-spin" v-if="processing"></i>
				<template v-else>
					<div class="icon mr-2" v-if="iconLeft">
						<i :class="iconLeft"></i>
					</div>

					<!-- Main Label with hover effect -->
					<div class="label-container">
						<div class="label-content">
							<label class="label-main"><slot /></label>
							<label class="label-hover" aria-hidden="true"><slot /></label>
						</div>
					</div>

					<!-- Main Icon -->
					<div class="icon" v-if="icon">
						<i :class="icon"></i>
					</div>

					<div class="icon ml-2" v-if="iconRight">
						<i :class="iconRight"></i>
					</div>
				</template>
			</div>
		</div>
	</router-link>
	<component v-else :is="href ? 'a' : 'button'" :class="['btn group', { mega: mega != null }, type]" :disabled="disabled" :target="href ? (target ?? '_blank') : null" :href :style="[accent ? { backgroundColor: accent, borderColor: accent } : null]">
		<div class="relative flex">
			<!-- Content -->
			<div :class="['content']">
				<i class="icon far fa-circle-notch fa-spin group animate-spin" v-if="processing"></i>
				<template v-else>
					<div class="icon mr-2" v-if="iconLeft">
						<i :class="iconLeft"></i>
					</div>

					<!-- Main Label with hover effect -->
					<div class="label-container">
						<div class="label-content">
							<label class="label-main"><slot /></label>
							<label class="label-hover" aria-hidden="true"><slot /></label>
						</div>
					</div>

					<!-- Main Icon -->
					<div class="icon" v-if="icon">
						<i :class="icon"></i>
					</div>

					<div class="icon ml-2" v-if="iconRight">
						<i :class="iconRight"></i>
					</div>
				</template>
			</div>
		</div>
	</component>
</template>

<style scoped>
	@reference "../../../css/app.css";

/* Global / Default */
.btn {
	@apply h-fit w-fit cursor-pointer overflow-hidden rounded-md text-2xl transition-all duration-500 ease-in-out;
}
.btn .content {
	@apply flex h-12 items-center justify-center px-6;
}
.btn .icon {
	@apply ml-2 text-[1rem];
}
.btn.grow,
.btn.grow .content {
	@apply grow;
}
.btn.full,
.btn.full .content {
	@apply !w-full;
}
.btn.solid {
	@apply bg-logi-blue-500 text-white;
}
.btn.solid.light {
	@apply bg-neutral-500 text-logi-blue-600;
}
.btn.solid.dark {
	@apply bg-black text-white;
}
.btn.solid.grey {
	@apply bg-logi-grey-500 text-white;
}
.btn.secondary {
	@apply border border-logi-blue-500 text-logi-blue-900;
}
.btn.secondary label {
	@apply font-bold;
}
.btn.secondary.light {
	@apply border border-logi-blue-500 text-neutral-500;
}
.btn.medium {
	@apply w-full rounded-md;
}
.btn.medium .content {
	@apply h-10 w-full px-5;
}
.btn.thin {
	@apply min-w-fit rounded-md;
}
.btn.thin .content {
	@apply h-8 w-fit px-5;
}
label {
	@apply mb-0.5 text-sm font-medium;
}

/* Label */
.label-container {
	@apply relative overflow-hidden pt-1 leading-tight;
}
.label-content {
	@apply flex flex-col transition-transform duration-300 ease-out;
}
.label-main,
.label-hover {
	@apply flex cursor-pointer items-center whitespace-nowrap;
}
.label-hover {
	@apply absolute top-full;
}
.btn:not(:disabled):hover .label-content {
	@apply -translate-y-full;
}

/* Disabled */
.btn:disabled {
	@apply cursor-not-allowed border-logi-grey-500 bg-logi-grey-500 text-white/20;
}
.btn:disabled .label-main,
.btn:disabled .label-hover {
	@apply cursor-not-allowed;
}
</style>
